// One part of a document, edited.
//
// The panels came out of DocumentComposer, which was a full-screen shell with
// a palette down one side, a list in the middle and a properties column on the
// right — an editor of its own, over the top of the editor you were already
// in. The parts list is inline on the Document tab now, so what is left is
// the thing the composer was actually for: the fields one part needs. They
// live here, in a modal, because that is where every edit in this app happens
// (rule 6) and because two screens must not grow two copies of them.
//
// Nothing here renders a PDF and nothing here saves. What it produces is a
// DocBlock, handed back to the tab, which writes it with Save document.
import { useEffect, useState } from 'react';
import type { DocBlock, FormPdfTemplate } from '../../api/forms';
import { templateForPart } from '../../api/forms';
import { Modal } from '../../components/fs';
import { TemplateInput } from './ConnectionEditors';
import type { FieldGroup } from './ConnectionEditors';
import type { ScopeOption } from './PlacementEditor';
import FieldPicker from './FieldPicker';
import type { OptGroup } from './fieldGroups';
import { filterGroups } from './fieldGroups';
import { pageThumbnails } from './pdfjs';

export type DocBlockKind = DocBlock['type'];

export const KIND_LABEL: Record<DocBlockKind, string> = {
  template: 'Template pages',
  answers: 'The answers',
  heading: 'Heading',
  paragraph: 'Paragraph',
  table: 'Table of items',
  terms: 'Terms',
  signature: 'Signature',
  pageBreak: 'Page break',
};

/** Everything a part's editor needs to know about the form it belongs to. */
export interface DocBlockContext {
  /** The document's PDFs, blank pages included, in order. */
  templates: FormPdfTemplate[];
  fields: { key: string; label: string }[];
  /** Keys of the questions set Hidden on the form — the ones a respondent never sees. */
  hiddenKeys: string[];
  signatureKeys: string[];
  scopes: ScopeOption[];
  sections: { id: string; title: string }[];
  /** The answers arranged the way the form is built: the main form, each repeating container, and both together. */
  answerGroups: OptGroup[];
  scopeGroups: Record<string, OptGroup[]>;
  allGroups: OptGroup[];
  /** What a heading or paragraph can insert. */
  tokenGroups: FieldGroup[];
}

/**
 * One line saying what a part IS, for the list.
 *
 * Deliberately concrete — "pages 1, 2, 3", "Devices · 4 columns" — because the
 * list is the only place the arrangement can be read at a glance, and a row
 * that said only "Table of items" would send somebody into the modal to find
 * out which table.
 */
export function blockSummary(b: DocBlock, ctx: DocBlockContext): string {
  const labelOf = (key: string) => ctx.fields.find((f) => f.key === key)?.label || key;
  switch (b.type) {
    case 'template': {
      const t = templateForPart(ctx.templates, b);
      if (!t) return 'a PDF this form no longer has';
      const pages = b.pages?.length === 1 ? `page ${b.pages[0]}` : b.pages?.length ? `pages ${b.pages.join(', ')}` : t.pages === 1 ? 'its page' : `all ${t.pages} pages`;
      return ctx.templates.length > 1 || t.blank ? `${templateName(t)} · ${pages}` : pages;
    }
    case 'answers': {
      const which = b.sections?.length ? `${b.sections.length} section${b.sections.length === 1 ? '' : 's'}` : 'every section';
      return which + (b.hide?.length ? ` · ${b.hide.length} left off` : '') + (b.showHidden ? ' · prints hidden answers' : '');
    }
    case 'heading': return b.text || '(empty)';
    case 'paragraph': return b.text ? `${b.text.slice(0, 60)}${b.text.length > 60 ? '…' : ''}` : '(empty)';
    case 'table': {
      const scope = ctx.scopes.find((s) => s.scope === b.scope);
      return `${scope?.label || b.scope} · ${b.columns.length} column${b.columns.length === 1 ? '' : 's'}`
        + (b.total ? ' · totals' : '');
    }
    case 'terms': return labelOf(b.fieldKey);
    case 'signature': return labelOf(b.fieldKey);
    case 'pageBreak': return 'starts a new page';
    default: return '';
  }
}

/** What a PDF is called on this tab: its file name, or what a blank page is. */
export const templateName = (t: FormPdfTemplate): string =>
  t.blank ? (t.pages === 1 ? 'Blank page' : `Blank pages (${t.pages})`) : (t.name || 'template.pdf');

/** Which pill a part wears: your PDF, or built from the answers. */
export const isTemplatePart = (b: DocBlock): boolean => b.type === 'template';

/** A fresh part of this kind, or null when the form has nothing to build it from. */
export function newBlock(kind: DocBlockKind, ctx: DocBlockContext): DocBlock | null {
  switch (kind) {
    case 'template': return ctx.templates[0] ? { type: 'template', templateId: ctx.templates[0].id } : null;
    case 'answers': return { type: 'answers' };
    case 'heading': return { type: 'heading', text: 'Heading' };
    case 'paragraph': return { type: 'paragraph', text: '' };
    case 'table': {
      const sc = ctx.scopes[0];
      if (!sc) return null;
      return { type: 'table', scope: sc.scope, columns: sc.fields.slice(0, 2).map((f) => ({ fieldKey: f.key })) };
    }
    case 'terms': return ctx.signatureKeys[0] ? { type: 'terms', fieldKey: ctx.signatureKeys[0] } : null;
    case 'signature': return ctx.signatureKeys[0] ? { type: 'signature', fieldKey: ctx.signatureKeys[0] } : null;
    case 'pageBreak': return { type: 'pageBreak' };
    default: return null;
  }
}

/**
 * Which kinds can be added, and why not.
 *
 * The reasons are the composer's own and they matter: "this form has no
 * signature field" sends somebody to the Build tab, whereas a kind that simply
 * vanished from the menu would send them nowhere.
 */
export function palette(
  ctx: DocBlockContext, canUseTemplate: boolean
): { kind: DocBlockKind; hint?: string; disabled?: boolean }[] {
  return [
    {
      kind: 'template',
      disabled: !ctx.templates.length || !canUseTemplate,
      hint: !ctx.templates.length
        ? 'Upload a PDF or add a blank page above first.'
        : !canUseTemplate ? 'Your own PDF is on every paid plan.' : undefined,
    },
    { kind: 'answers' },
    { kind: 'heading' },
    { kind: 'paragraph' },
    { kind: 'table', disabled: !ctx.scopes.length, hint: ctx.scopes.length ? undefined : 'This form has no repeating items.' },
    { kind: 'signature', disabled: !ctx.signatureKeys.length, hint: ctx.signatureKeys.length ? undefined : 'This form has no signature field.' },
    { kind: 'terms', disabled: !ctx.signatureKeys.length, hint: ctx.signatureKeys.length ? undefined : 'This form has no signature field.' },
    { kind: 'pageBreak' },
  ];
}

/**
 * The modal around one part's fields.
 *
 * Edits are held locally and handed back on Save, so Cancel really cancels —
 * and even Save writes nothing: the tab collects the change and the page's own
 * Save document is what reaches the server.
 */
export default function DocBlockEditor({ block, index, ctx, onCancel, onSave }: {
  block: DocBlock;
  /** 1-based, for the title — "Part 3 · Table of items". */
  index: number;
  ctx: DocBlockContext;
  onCancel: () => void;
  onSave: (next: DocBlock) => void;
}) {
  const [draft, setDraft] = useState<DocBlock>(block);

  return (
    <Modal
      narrow
      title={KIND_LABEL[draft.type]}
      aside={`Part ${index}`}
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="fs-btn primary" onClick={() => onSave(draft)}>Done</button>
          <button type="button" className="fs-btn neutral" onClick={onCancel}>Cancel</button>
          <span className="fs-cell-sub" style={{ marginLeft: 'auto' }}>
            Applied when you save the document.
          </span>
        </>
      )}
    >
      <BlockPanel block={draft} onChange={setDraft} ctx={ctx} />
    </Modal>
  );
}

/**
 * The fields one kind of part needs. Lifted from DocumentComposer unchanged —
 * these are the panels that screen existed to show, and they were the half of
 * it worth keeping.
 */
export function BlockPanel({ block, onChange, ctx }: {
  block: DocBlock;
  onChange: (next: DocBlock) => void;
  ctx: DocBlockContext;
}) {
  const { templates, fields, signatureKeys, scopes, sections, tokenGroups, answerGroups, scopeGroups, allGroups } = ctx;
  const template = block.type === 'template' ? templateForPart(templates, block) : undefined;
  const signatureGroups = filterGroups(allGroups, (v) => signatureKeys.includes(v));
  const labelOf = (key: string) => fields.find((f) => f.key === key)?.label || key;
  /** Thumbnails of one PDF, and which PDF they are of. */
  const [thumbs, setThumbs] = useState<{ url: string; pages: string[] } | null>(null);

  // Thumbnails only for the template panel — pdf.js is large, and most parts
  // never need it. Redrawn when the part is pointed at another PDF.
  useEffect(() => {
    if (block.type !== 'template' || !template?.url || thumbs?.url === template.url) return;
    let cancelled = false;
    const url = template.url;
    setThumbs(null);
    pageThumbnails(url)
      .then((t) => { if (!cancelled) setThumbs({ url, pages: t }); })
      // No pictures is not no picker: the page numbers still work.
      .catch(() => { if (!cancelled) setThumbs({ url, pages: [] }); });
    return () => { cancelled = true; };
  }, [block.type, template?.url, thumbs?.url]);

  switch (block.type) {
    case 'template': {
      const total = template?.pages || 0;
      const chosen = new Set(block.pages || []);
      const all = !block.pages?.length;
      const toggle = (p: number) => {
        const next = new Set(all ? Array.from({ length: total }, (_, i) => i + 1) : chosen);
        if (next.has(p)) next.delete(p); else next.add(p);
        const list = [...next].sort((a, b) => a - b);
        // Every page chosen reads as "all" — stored as absence, so a template
        // that grows a page later brings the new one along.
        onChange({
          type: 'template', templateId: block.templateId,
          ...(list.length && list.length < total ? { pages: list } : {}),
        });
      };
      return (
        <>
          {templates.length > 1 && (
            <label className="pe-prop">
              <span>Which PDF</span>
              <select
                value={template?.id || ''}
                // Another PDF has other pages: the choice starts again at all of them.
                onChange={(e) => onChange({ type: 'template', templateId: e.target.value })}
              >
                {!template && <option value="">— choose a PDF —</option>}
                {templates.map((t) => <option key={t.id} value={t.id}>{templateName(t)}</option>)}
              </select>
            </label>
          )}
          <div className="pe-hint">
            Which of {template ? <>{templateName(template)}&rsquo;s</> : 'its'} {total} page{total === 1 ? '' : 's'} to include here, in order.
          </div>
          <div className="dc-pages">
            {Array.from({ length: total }, (_, i) => i + 1).map((p) => {
              const on = all || chosen.has(p);
              return (
                <label key={p} className={`dc-page ${on ? 'is-on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(p)} aria-label={`Page ${p}`} />
                  {thumbs?.url === template?.url && thumbs?.pages[p - 1]
                    ? <img src={thumbs.pages[p - 1]} alt="" />
                    : <span className="dc-page-blank">{thumbs?.url === template?.url ? 'no preview' : 'loading…'}</span>}
                  <span className="dc-page-no">{p}</span>
                </label>
              );
            })}
          </div>
        </>
      );
    }
    case 'answers': {
      const chosen = new Set(block.sections || []);
      const all = !block.sections?.length;
      return (
        <>
          <div className="pe-hint">The record&rsquo;s sections, as the form asked them. Untick any to leave out.</div>
          {sections.map((s) => (
            <label key={s.id} className="pe-prop pe-prop-check">
              <input
                type="checkbox" checked={all || chosen.has(s.id)}
                onChange={(e) => {
                  const next = new Set(all ? sections.map((x) => x.id) : chosen);
                  if (e.target.checked) next.add(s.id); else next.delete(s.id);
                  const list = sections.map((x) => x.id).filter((id) => next.has(id));
                  onChange({
                    type: 'answers',
                    ...(list.length && list.length < sections.length ? { sections: list } : {}),
                    ...(block.hide?.length ? { hide: block.hide } : {}),
                    ...(block.showHidden ? { showHidden: true } : {}),
                  });
                }}
              />
              <span>{s.title || s.id}</span>
            </label>
          ))}
          {!sections.length && (
            <div className="pe-hint">This form has no sections yet — every answer is included.</div>
          )}
          <AnswerVisibility block={block} onChange={onChange} ctx={ctx} />
        </>
      );
    }
    case 'heading':
      return (
        <TemplateInput
          label="Text" value={block.text} groups={tokenGroups}
          onChange={(text) => onChange({ type: 'heading', text })}
        />
      );
    case 'paragraph':
      return (
        <TemplateInput
          label="Text" multiline rows={8} value={block.text} groups={tokenGroups}
          hint="Line breaks are kept. Tokens fill from the submission. Plain text — no formatting in this version."
          onChange={(text) => onChange({ type: 'paragraph', text })}
        />
      );
    case 'table': {
      const sc = scopes.find((s) => s.scope === block.scope);
      const options = sc?.fields.length ? sc.fields : fields;
      const columnGroups = sc && scopeGroups[sc.scope]?.length ? scopeGroups[sc.scope] : answerGroups;
      const setCols = (columns: typeof block.columns) => onChange({ ...block, columns });
      return (
        <>
          <label className="pe-prop">
            <span>One row per</span>
            <select
              value={block.scope}
              onChange={(e) => {
                const next = scopes.find((s) => s.scope === e.target.value);
                onChange({
                  type: 'table', scope: e.target.value,
                  columns: next?.fields.slice(0, 2).map((f) => ({ fieldKey: f.key })) || [],
                });
              }}
            >
              {scopes.map((s) => <option key={s.scope} value={s.scope}>{s.label}</option>)}
            </select>
          </label>
          <div className="pe-overline" style={{ marginTop: 12 }}>Columns</div>
          {block.columns.map((c, k) => (
            <div className="pe-col" key={k}>
              <FieldPicker
                value={c.fieldKey} groups={columnGroups} allowClear={false}
                onChange={(v) => setCols(block.columns.map((x, j) => (j === k ? { ...x, fieldKey: v } : x)))}
              />
              <input
                placeholder={`Heading (default: ${labelOf(c.fieldKey)})`} value={c.label || ''}
                onChange={(e) => setCols(block.columns.map((x, j) => (j === k ? { ...x, label: e.target.value || undefined } : x)))}
              />
              <div className="pe-col-row">
                <select
                  value={c.format || 'text'}
                  onChange={(e) => setCols(block.columns.map((x, j) => (j === k ? { ...x, format: e.target.value } : x)))}
                >
                  <option value="text">As typed</option>
                  <option value="date">Date</option>
                  <option value="number">Number</option>
                  <option value="currency">Amount</option>
                  <option value="checkbox">Tick</option>
                </select>
                <select
                  value={c.align || 'left'}
                  onChange={(e) => setCols(block.columns.map((x, j) => (j === k
                    ? { ...x, align: e.target.value as 'left' | 'center' | 'right' } : x)))}
                >
                  <option value="left">L</option>
                  <option value="center">C</option>
                  <option value="right">R</option>
                </select>
                <button type="button" className="pe-col-x" title="Remove this column"
                  onClick={() => setCols(block.columns.filter((_, j) => j !== k))}>×</button>
              </div>
            </div>
          ))}
          <button
            type="button" className="fs-btn" disabled={!options.length}
            onClick={() => setCols([...block.columns, { fieldKey: (options[block.columns.length] || options[0]).key }])}
          >
            + Add a column
          </button>
          <div className="pe-overline" style={{ marginTop: 14 }}>Total row</div>
          <label className="pe-prop">
            <span>Totals which column</span>
            <select
              value={block.total?.column || ''}
              onChange={(e) => onChange({
                ...block,
                total: e.target.value
                  ? { column: e.target.value, op: block.total?.op || 'sum', label: block.total?.label }
                  : undefined,
              })}
            >
              <option value="">No total</option>
              {block.columns.map((c, k) => <option key={k} value={c.fieldKey}>{labelOf(c.fieldKey)}</option>)}
            </select>
          </label>
          {block.total && (
            <>
              <label className="pe-prop">
                <span>Add up or count</span>
                <select
                  value={block.total.op}
                  onChange={(e) => onChange({ ...block, total: { ...block.total!, op: e.target.value as 'sum' | 'count' } })}
                >
                  <option value="sum">Add the values up</option>
                  <option value="count">Count the rows</option>
                </select>
              </label>
              <label className="pe-prop">
                <span>Label (optional)</span>
                <input
                  value={block.total.label || ''} placeholder="Total monthly fees"
                  onChange={(e) => onChange({ ...block, total: { ...block.total!, label: e.target.value || undefined } })}
                />
              </label>
            </>
          )}
        </>
      );
    }
    case 'signature':
      return (
        <>
          <label className="pe-prop">
            <span>Which signature</span>
            <FieldPicker value={block.fieldKey} groups={signatureGroups} allowClear={false} onChange={(fieldKey) => onChange({ ...block, fieldKey })} />
          </label>
          <label className="pe-prop pe-prop-check">
            <input
              type="checkbox" checked={block.showAudit !== false}
              onChange={(e) => onChange({ ...block, ...(e.target.checked ? { showAudit: undefined } : { showAudit: false }) })}
            />
            <span>Print when and where it was signed beneath it</span>
          </label>
        </>
      );
    case 'terms':
      return (
        <>
          <label className="pe-prop">
            <span>Terms of which signature</span>
            <FieldPicker value={block.fieldKey} groups={signatureGroups} allowClear={false} onChange={(fieldKey) => onChange({ type: 'terms', fieldKey })} />
          </label>
          <div className="pe-hint">
            Prints the wording that signature agrees to, as it read when signed. The wording itself is
            set on the signature field.
          </div>
        </>
      );
    case 'pageBreak':
      return <div className="pe-hint">Whatever follows starts on a new page.</div>;
    default:
      return null;
  }
}

/**
 * Which answers this part prints, question by question.
 *
 * Every question starts shown — that is what the part has always printed —
 * and a tick taken off leaves it off the page. The questions set Hidden on
 * the form are marked, and one button leaves all of them off at once: they
 * are the usual reason for this list (a rep code, a price the customer never
 * saw), and finding them one by one in a long form is the chore it saves.
 */
function AnswerVisibility({ block, onChange, ctx }: {
  block: Extract<DocBlock, { type: 'answers' }>;
  onChange: (next: DocBlock) => void;
  ctx: DocBlockContext;
}) {
  const known = new Set(ctx.fields.map((f) => f.key));
  // Whole questions only — a composite's parts ('address.city') come and go
  // with their question.
  const groups = ctx.allGroups
    .map((g) => ({ ...g, opts: g.opts.filter((o) => known.has(o.v)) }))
    .filter((g) => g.opts.length);
  const hidden = new Set(block.hide || []);
  const onForm = ctx.hiddenKeys.filter((k) => known.has(k));
  const set = (next: Set<string>) => {
    const hide = ctx.fields.map((f) => f.key).filter((k) => next.has(k));
    const { hide: _was, ...rest } = block;
    onChange(hide.length ? { ...rest, hide } : rest);
  };
  const { showHidden: _shown, ...withoutShown } = block;
  return (
    <>
      {/* What the Logic hid is decided per submission, from what the
          respondent's screen showed when they sent it (the page records it).
          Off by default — the rule is "print what they were shown". */}
      <label className="pe-prop pe-prop-check" style={{ marginTop: 14 }}>
        <input
          type="checkbox" checked={!!block.showHidden}
          onChange={(e) => onChange(e.target.checked ? { ...withoutShown, showHidden: true } : withoutShown)}
        />
        <span>Also print answers the respondent could not see</span>
      </label>
      <div className="pe-hint">
        Off: an answer to a question the form&rsquo;s Logic hid when they submitted — a hidden
        calculation, a prefilled code — is left off this list. Placed boxes, tables and the file
        name still use it.
      </div>
      {groups.length > 0 && (
      <>
      <div className="pe-overline" style={{ marginTop: 14 }}>Questions</div>
      <div className="pe-hint">
        Untick a question to leave its answer off every time, even when the respondent could see it.
        {hidden.size > 0 && <> <b>{hidden.size} left off.</b></>}
      </div>
      <div className="dc-answer-acts">
        {onForm.length > 0 && (
          <button
            type="button" className="fs-btn sm"
            disabled={onForm.every((k) => hidden.has(k))}
            onClick={() => set(new Set([...hidden, ...onForm]))}
          >
            Always leave off the {onForm.length} set Hidden on the form
          </button>
        )}
        <button type="button" className="fs-btn neutral sm" disabled={!hidden.size} onClick={() => set(new Set())}>
          Show every answer
        </button>
      </div>
      <div className="dc-answers">
        {groups.map((g) => (
          <div key={g.label} className="dc-answer-group">
            {groups.length > 1 && <div className="dc-answer-group-label">{g.label}</div>}
            {g.opts.map((o) => (
              <label key={o.v} className="pe-prop pe-prop-check">
                <input
                  type="checkbox" checked={!hidden.has(o.v)}
                  onChange={(e) => {
                    const next = new Set(hidden);
                    if (e.target.checked) next.delete(o.v); else next.add(o.v);
                    set(next);
                  }}
                />
                <span>
                  {/* The picker's label carries the key for telling two
                      "Email"s apart in a dropdown; a list that shows every
                      question at once reads better without it. */}
                  {o.l.endsWith(` (${o.v})`) ? o.l.slice(0, -(o.v.length + 3)) : o.l}
                  {ctx.hiddenKeys.includes(o.v) && <span className="fs-pill neutral dc-answer-pill">hidden on the form</span>}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
      </>
      )}
    </>
  );
}
